import * as v from 'valibot'
import { Account } from './account.js'

/** 第三方快速登录支持的平台名单，全仓库唯一出处（plan/oauth-quick-login/01-technical-design.html）。 */
export const OAUTH_PROVIDERS = ['google', 'apple', 'x', 'discord', 'facebook', 'tiktok']

/**
 * 登录请求：provider（oauth 厂商 / 匿名）+ 凭据 + 可选设备标识（匿名身份合并用）。
 * @typedef {{ provider: string, credential: string, device_id?: string }} LoginRequestT
 */
export const LoginRequest = v.object({
  provider: v.string(),
  credential: v.string(),
  device_id: v.optional(v.string()),
})

/**
 * 第三方快速登录请求：授权码 + PKCE verifier（Google/X 必填，其余平台不用）。
 * @typedef {{ provider: 'google'|'apple'|'x'|'discord'|'facebook'|'tiktok', code?: string, redirect_uri?: string, code_verifier?: string, access_token?: string, locale?: 'en'|'zh'|'ja'|'ko'|'es'|'pt', device_id?: string }} OauthLoginRequestT
 */
export const OauthLoginRequest = v.pipe(
  v.strictObject({
    provider: v.picklist(OAUTH_PROVIDERS),
    // 授权码流程带 code + redirect_uri；Google 桌面弹窗（GIS）流程只带 access_token
    code: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(2048))),
    redirect_uri: v.optional(v.pipe(v.string(), v.url())),
    code_verifier: v.optional(v.pipe(v.string(), v.minLength(43), v.maxLength(128))),
    access_token: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(4096))),
    locale: v.optional(v.picklist(['en', 'zh', 'ja', 'ko', 'es', 'pt'])),
    device_id: v.optional(v.string()),
  }),
  v.check(input => Boolean(input.access_token) || Boolean(input.code && input.redirect_uri), 'code + redirect_uri or access_token is required'),
)

/**
 * 登录响应：访问 / 刷新 token 对 + 账户资料。
 * @typedef {{ access_token: string, refresh_token: string, account: import('./account.js').AccountT, is_new_user: boolean }} LoginResponseT
 */
export const LoginResponse = v.object({
  access_token: v.string(),
  refresh_token: v.string(),
  account: Account,
  is_new_user: v.optional(v.boolean(), false),
})

/**
 * 开放注册请求：验证码只用于本次邮箱注册，邀请码不属于该契约。
 * @typedef {{ email: string, password: string, verification_code: string, locale: 'en'|'zh'|'ja'|'ko'|'es'|'pt', device_id?: string }} RegisterRequestT
 */
export const RegisterRequest = v.strictObject({
  email: v.pipe(v.string(), v.trim(), v.toLowerCase(), v.email(), v.maxLength(254)),
  password: v.pipe(v.string(), v.minLength(8), v.maxLength(256)),
  verification_code: v.pipe(v.string(), v.regex(/^\d{6}$/)),
  locale: v.picklist(['en', 'zh', 'ja', 'ko', 'es', 'pt']),
  device_id: v.optional(v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(255))),
})

/** @typedef {{ channel: 'email', target: string, purpose: 'register'|'login', locale?: 'en'|'zh'|'ja'|'ko'|'es'|'pt' }} SendRegistrationCodeRequestT */
export const SendRegistrationCodeRequest = v.strictObject({
  channel: v.literal('email'),
  target: v.pipe(v.string(), v.trim(), v.toLowerCase(), v.email(), v.maxLength(254)),
  purpose: v.picklist(['register', 'login']),
  locale: v.optional(v.picklist(['en', 'zh', 'ja', 'ko', 'es', 'pt'])),
})

/**
 * 邮箱验证码登录：登录和注册同一条路，邮箱不存在即建号。
 * @typedef {{ email: string, verification_code: string, locale?: 'en'|'zh'|'ja'|'ko'|'es'|'pt', device_id?: string }} EmailCodeLoginRequestT
 */
export const EmailCodeLoginRequest = v.strictObject({
  email: v.pipe(v.string(), v.trim(), v.toLowerCase(), v.email(), v.maxLength(254)),
  verification_code: v.pipe(v.string(), v.regex(/^\d{6}$/)),
  locale: v.optional(v.picklist(['en', 'zh', 'ja', 'ko', 'es', 'pt'])),
  device_id: v.optional(v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(255))),
})
